import { computed, reactive } from 'vue';
import { IBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import {
    GiftPackingItemOption,
    GiftPackingMessage,
} from '@/storecontext/shared/api/server-apis';
import utilitiesService from '@/shared/utilities.service';

export enum GiftPackagingViewMode {
    Details = 0,
    Confirm = 1,
}

export interface GiftPackagingStateDetails {
    option: GiftPackingItemOption;
    message: GiftPackingMessage | undefined;
}

export interface GiftPackagingState {
    registered: boolean;
    labels: IBlockLabels;
    activeItemId: number | null;
    details: GiftPackagingStateDetails;
    viewMode: GiftPackagingViewMode;
}

const state: GiftPackagingState = reactive({
    registered: false,
    labels: {} as IBlockLabels,
    activeItemId: null,
    details: {} as GiftPackagingStateDetails,
    viewMode: GiftPackagingViewMode.Details,
});

export const maxAmountOfMessageLines = 5;
export const maxCharactersPerLine = 40;

export default function useGiftPackaging() {
    const registered = computed<boolean>(() => state.registered);
    const labels = computed<IBlockLabels>(() => state.labels);
    const activeItemId = computed<number | null>(() => state.activeItemId);
    const hasActiveItemId = computed<boolean>(() => state.activeItemId !== null);
    const details = computed<GiftPackagingStateDetails>(() => state.details);
    const viewMode = computed<GiftPackagingViewMode>(() => state.viewMode);
    const emojisRegex = computed<string>(() => labels.value.cardMessageRegex);

    const register = (labels: IBlockLabels): void => {
        state.labels = labels;
        state.registered = true;
    };

    const unregister = (): void => {
        state.registered = false;
    };

    const setActiveItemId = (id: number): void => {
        state.activeItemId = id;
    };

    const clearActiveItemId = (): void => {
        state.activeItemId = null;
    };

    const setViewMode = (viewMode: GiftPackagingViewMode): void => {
        state.viewMode = viewMode;
    };

    const activateGiftPackaging = (itemId: number, details: GiftPackagingStateDetails, viewMode: GiftPackagingViewMode = GiftPackagingViewMode.Details): void => {
        state.details = details;
        setViewMode(viewMode);
        setActiveItemId(itemId);
    };

    const getFormattedMessage = (text: string): string[] | undefined => {
        const formattedMessage = [''];
        let activeMessageLineNumber = 0;

        const emojiFreeText = utilitiesService.stripEmojis(text, emojisRegex.value);
        const linesByUser = emojiFreeText?.split('\n');

        linesByUser.forEach((line: string) => {
            const wordsInLine = line.split(/\s+/);
            wordsInLine.forEach((word: string) => {
                addWord(word);
            });
            bumpActiveLine();
        });
        removeEmptyLinesInEndOfMessage();

        function removeEmptyLinesInEndOfMessage() {
            if (formattedMessage[formattedMessage.length - 1] === '') {
                formattedMessage.pop();
                removeEmptyLinesInEndOfMessage();
            }
        }

        function addWord(word: string) {
            const wordFitsInActiveLine = getWordFitsInActiveLine(word.length);
            const wordLengthExceedsMax = word.length > maxCharactersPerLine;
            if (wordLengthExceedsMax) {
                breakWordIntoMultipleLines(word);
            } else if (wordFitsInActiveLine) {
                pushWordToActiveLine(word);
            } else {
                bumpActiveLine();
                pushWordToActiveLine(word);
            }
        }

        function bumpActiveLine(): void {
            trimActiveLine();
            activeMessageLineNumber++;
            formattedMessage.push('');
        }

        function trimActiveLine() {
            const stringToTrim = formattedMessage[activeMessageLineNumber];
            formattedMessage[activeMessageLineNumber] = stringToTrim.trim();
        }

        function getWordFitsInActiveLine(wordLength: number): boolean {
            const activeMessageLine: string = formattedMessage[activeMessageLineNumber];
            return activeMessageLine.length + wordLength <= maxCharactersPerLine;
        }

        function breakWordIntoMultipleLines(word: string) {
            const activeMessageLine: string = formattedMessage[activeMessageLineNumber];
            const remainingSpaceInActiveLine: number = maxCharactersPerLine - activeMessageLine.length;
            const wordPartialForActiveLine: string = word.substring(0, remainingSpaceInActiveLine);
            const remainingWord: string = word.substring(remainingSpaceInActiveLine, word.length);
            formattedMessage[activeMessageLineNumber] += wordPartialForActiveLine + ' ';
            bumpActiveLine();
            if (getWordFitsInActiveLine(remainingWord.length)) {
                pushWordToActiveLine(remainingWord);
            } else {
                const listOfRemainingWords = remainingWord.match(new RegExp('.{1,' + maxCharactersPerLine + '}', 'g'));
                if (listOfRemainingWords) {
                    listOfRemainingWords.forEach((wordPartial: string) => {
                        pushWordToActiveLine(wordPartial);
                        if (wordPartial.length >= maxCharactersPerLine) {
                            bumpActiveLine();
                        }
                    });
                }
            }
        }

        function pushWordToActiveLine(word: string): void {
            formattedMessage[activeMessageLineNumber] += word + ' ';
        }
        return formattedMessage;
    };
    return {
        registered,
        labels,
        activeItemId,
        hasActiveItemId,
        details,
        viewMode,
        register,
        unregister,
        setActiveItemId,
        clearActiveItemId,
        setViewMode,
        activateGiftPackaging,
        getFormattedMessage,
    };
}
